import { pick } from 'lodash';

import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

export const completeOnboarding = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/user-booleans/`,
      method: RequestMethod.POST,
      data: {
        type: 'student_main_onboarding',
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminGrades = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/sl_users/students/grades/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminCaseloads = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/sl_users/students/caseloads`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminClassYears = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/students/class-years`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const searchStudents = ({
  params = {},
  version = 'v1',
  nextUrl,
  pageSize = 20,
} = {}) => {
  const newUrl = `${version}/sl_users/bulk-search-students`;

  return client
    .request({
      url: nextUrl || newUrl,
      method: RequestMethod.POST,
      params: nextUrl
        ? {}
        : {
            ...pick(params, [
              'page',
              'ordering',
              'pageSize',
              'studentScope',
              'activeStudentsOnly',
            ]),
            pageSize,
          },
      data: {
        searchFilters: params,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const bulkMatchStudents = ({
  version = 'v1',
  data = [],
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/bulk-search-students/bulk-match`,
      method: RequestMethod.POST,
      data: { identifier_list: data },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createStudentsList = ({ version = 'v1', data = [] } = {}) =>
  client
    .request({
      url: `${version}/sl_users/bulk-search-students/create-search-list`,
      method: RequestMethod.POST,
      data: { entity_ids: data },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteSelectedStudentsList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/bulk-search-students/delete-search-list`,
      method: RequestMethod.POST,
      data: { user_search_entity_list_type: 'selected' },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addStudentToSelectedStudentsList = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/bulk-search-students/add-to-search-list`,
      method: RequestMethod.POST,
      data: { entity_ids: [payload.entity.id] },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const selectAllStudents = ({ version = 'v1', params, apiParams } = {}) =>
  client
    .request({
      url: `${version}/sl_users/bulk-search-students/add-to-search-list`,
      method: RequestMethod.POST,
      data: {
        user_search_entity_list_type: 'selected',
        search_filters: {
          ...params,
        },
      },
      params: apiParams,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deselectAllStudents = ({
  version = 'v1',
  params,
  excludedEntityIdHash = undefined,
  apiParams,
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/bulk-search-students/remove-from-search-list`,
      method: RequestMethod.POST,
      data: {
        excluded_entity_id_hash: excludedEntityIdHash,
        user_search_entity_list_type: 'selected',
        search_filters: {
          ...params,
        },
      },
      params: apiParams,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteStudentFromSelectedStudentsList = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/bulk-search-students/remove-from-search-list`,
      method: RequestMethod.POST,
      data: { params, entity_ids: [payload.entity.id] },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createHash = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/bulk-search-students/create-hash`,
      method: RequestMethod.POST,
      params,
      data: {
        search_filters: {
          user_search_entity_list_type: 'selected',
        },
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportStudentsList = ({
  version = 'v1',
  studentsHash = [],
} = {}) =>
  client
    .request({
      url: `${version}/counselor_dashboard/student-list-csv/`,
      method: RequestMethod.POST,
      data: { studentsHash },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentK12AdminList = ({
  studentId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/students/${studentId}/k12-admins/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentAddress = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/user-address/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addStudentAddress = ({ params, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/user-address/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEmail = ({ params, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/emails/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getUserBooleans = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/user-booleans/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateUserBooleans = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/user-booleans/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminList = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportStaffList = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/csv-export/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const bulkReassignAccessType = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/access-type-bulk-reassign/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentList = ({ params = {}, version = 'v1' } = {}) => {
  if (version === 'v2') {
    return client
      .request({
        url: `${version}/sl_users/counselor-dashboard-students/list/`,
        method: RequestMethod.GET,
        params,
      })
      .then(handleJSONResponse)
      .catch(handleError);
  }
  return client
    .request({
      url: `${version}/sl_users/counselor-dashboard-students/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const getStudentListCount = ({ params = {}, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/counselor-dashboard-students/list-count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEmailList = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/emails/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEmailDetails = ({ id, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/emails/${id}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getUserPreferenceColumns = ({
  listViewSlug,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/user-preference/get-columns/${listViewSlug}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const setUserPreferenceColumns = ({
  payload,
  listViewSlug,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/user-preference/set-columns/${listViewSlug}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAuthToken = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/auth/token/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentScopeContext = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/student-scope-context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateMemberCareers = ({
  version = 'v1',
  memberUuid,
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/member/${memberUuid}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMemberDashboardContext = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/member-dashboard-context/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getClaimedStudentList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/guardian/students/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getGuardianList = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/sl_users/guardian-contact-information/ilp/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addGuardianEmails = ({ version = 'v1', payload = {} } = {}) =>
  client
    .request({
      url: `${version}/sl_users/guardian-contact-information/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteGuardianContact = ({
  version = 'v1',
  guardianContactUuid = '',
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/guardian-contact-information/${guardianContactUuid}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPendingInvites = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/pending-invitations/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const inviteUsers = ({ payload, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/member/invite/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const resendInvite = ({ payload, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/invitation-reminder/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAllRoles = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admin-roles/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const fetchRolesCsv = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admin-roles/csv-export/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteRole = ({ id, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/k12-admin-roles/${id}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateRole = ({ id, update, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/k12-admin-roles/${id}/`,
      method: RequestMethod.PATCH,
      data: update,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const activateRole = ({ id, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/k12-admin-roles/${id}/activate/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deactivateRole = ({ id, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/k12-admin-roles/${id}/deactivate/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminRolesContext = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admin-roles/context/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminsContext = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins-context/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRoleDetails = ({ id, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admin-roles/${id}`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createRole = ({ data, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admin-roles/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteStaffUser = ({ userId, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/${userId}`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminUserDetail = ({
  userId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/${userId}`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createStaffUser = ({ data, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateStaffUser = ({ data, userId, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/${userId}`,
      method: RequestMethod.PATCH,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const bulkUpdateStaffUsers = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/role-bulk-reassign/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMemberInfo = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/member/member-information/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const acceptTermsOfService = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/member/accept-terms-of-service/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminSchools = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/students/schools/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getGuardianCount = ({ version = 'v1', studentId = '' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/guardian-students/count/`,
      method: RequestMethod.GET,
      ...(studentId
        ? {
            params: {
              studentId,
            },
          }
        : {}),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createGuardianPlaceholderUser = ({
  version = 'v1',
  studentId,
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/students/${studentId}/guardian-placeholder-user/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentReminderList = ({
  version = 'v1',
  studentId = '',
  params,
} = {}) => {
  const url = studentId
    ? `${version}/sl_users/student/reminders/${studentId}/`
    : `${version}/sl_users/student/reminders/`;

  return client
    .request({
      url,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const getUpcomingRemindersCount = ({ version = 'v1', studentId }) =>
  client
    .request({
      url: `${version}/sl_users/student/reminders/reminders-count/${studentId}`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getUserDetails = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/sl_users/user/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateUserDetails = ({ version = 'v1', payload, params }) =>
  client
    .request({
      url: `${version}/sl_users/user/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createGuestUser = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/users/create-organization-member-guest-user/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {string} payload.email -  valid emailId to validate it's uniqueness
 */
export const validateEmail = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/emails/validate/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {Object<{current_user_mode: ['guardian', 'secondary', 'elementary']}>} payload
 * @returns
 */
export const updateCurrentUserMode = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/users/set-current-user-mode/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {string} payload.phone -  valid phone number to validate it's uniqueness
 */
export const validatePhone = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/phones/validate/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {string} payload.phone -  valid phone number nonunique
 */
export const validateNonuniquePhone = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/phones/validate-nonunique/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getLoginMethods = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/sl_users/auth/methods/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCasefileContext = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/sl_users/casefile-context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMemberAffiliations = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/sl_users/member/member-affiliations/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIntercomUserHash = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/intercom-user-hash/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMembers = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/sl_users/member/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRoleReassignProtectedUsers = ({
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/role-update-protected-users/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAccessTypeReassignProtectedUsers = ({
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/k12-admins/access-type-update-protected-users/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const loginWithMagicLink = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/magic-links/login/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const signupWithMagicLink = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/magic-links/signup/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const verifyEmail = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/verify/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const resetPassword = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/reset/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);
